import { useMsal } from "@azure/msal-react";
import { useCallback } from "react";
import { API_URL } from "../envConfig";
import { InteractionStatus } from "@azure/msal-browser";

async function getTokensRaw(msalInstance, inProgress) {
  await assureInstanceInitialized(msalInstance, inProgress);
  const tokens = await msalInstance.acquireTokenSilent({
    scopes: [
      "User.Read",
      "Files.Read",
      "Sites.Read.All",
      "Sites.ReadWrite.All",
      "Files.ReadWrite.All",
      "Files.ReadWrite",
    ],
    account: msalInstance.getAllAccounts()[0],
  });
  return {
    accessToken: tokens.accessToken,
    idToken: tokens.idToken,
  };
}

async function assureInstanceInitialized(instance, inProgress) {
  return new Promise((resolve) => {
    const checkVariable = () => {
      if (
        instance.controller.initialized &&
        inProgress === InteractionStatus.None &&
        instance.controller.getAllAccounts().length !== 0
      ) {
        resolve(instance);
      } else {
        setTimeout(checkVariable, 100);
      }
    };

    checkVariable();
  });
}

export async function forceRefreshTokens(msalInstance) {
  await msalInstance.acquireTokenSilent({
    scopes: [
      "User.Read",
      "Files.Read",
      "Sites.Read.All",
      "Sites.ReadWrite.All",
      "Files.ReadWrite.All",
      "Files.ReadWrite",
    ],
    account: msalInstance.getAllAccounts()[0],
    forceRefresh: true,
  });
}

async function uploadDriversLicense(
  base64EncodedPngImage,
  isFrontSide,
  instance,
  inProgress
) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return (
    await fetch(API_URL + `upload/license`, {
      method: "POST",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + idAndAccessToken.idToken,
        "X-BTB-Access-Token": idAndAccessToken.accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        image: base64EncodedPngImage,
        isFrontSide: isFrontSide,
      }),
    })
  ).json();
}

async function getDriversLicense(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return (
    await fetch(API_URL + `upload/license`, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + idAndAccessToken.idToken,
        "X-BTB-Access-Token": idAndAccessToken.accessToken,
        "Content-Type": "application/json",
      },
    })
  ).json();
}

async function uploadSignature(base64EncodedPngImage, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return (
    await fetch(API_URL + `upload/signature`, {
      method: "POST",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + idAndAccessToken.idToken,
        "X-BTB-Access-Token": idAndAccessToken.accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ image: base64EncodedPngImage }),
    })
  ).json();
}

async function getSignature(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return (
    await fetch(API_URL + `upload/signature`, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + idAndAccessToken.idToken,
        "X-BTB-Access-Token": idAndAccessToken.accessToken,
        "Content-Type": "application/json",
      },
    })
  ).json();
}

async function saveData(jsonString, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return await fetch(API_URL + "baseData/save", {
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
    body: jsonString,
  });
}

async function getBaseData(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const baseDataObj = await fetch(API_URL + "baseData", {
    method: "GET",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
  });

  return baseDataObj.json();
}

async function getPdf(fileName, cacheType = "default", instance, inProgress) {
  let cacheControl = "";
  if (cacheType === "reload") {
    cacheControl = "no-cache";
  } else if (cacheType === "default") {
    cacheControl = "max-age=3600";
  }
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return (
    await fetch(API_URL + "pdf/data/" + fileName, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + idAndAccessToken.idToken,
        "X-BTB-Access-Token": idAndAccessToken.accessToken,
        "Content-Type": "application/json",
        Pragma: cacheControl,
        "Cache-Control": cacheControl,
      },
    })
  ).blob();
}

async function getPdfRequiredAnnotations(fileName, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return (
    await fetch(API_URL + "pdf/fields/non-signature/" + fileName, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + idAndAccessToken.idToken,
        "X-BTB-Access-Token": idAndAccessToken.accessToken,
        "Content-Type": "application/json",
      },
    })
  ).json();
}

// TODO: add pdfupload post method
async function uploadPdf(pdfFile, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const file = new FormData();
  file.append("file", pdfFile);
  return await fetch(API_URL + "pdf/upload/", {
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
    },
    body: file,
  });
}

async function signPdf(fileName, annotations, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return (
    await fetch(API_URL + "pdf/sign/" + fileName, {
      method: "POST",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + idAndAccessToken.idToken,
        "X-BTB-Access-Token": idAndAccessToken.accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ annotations: annotations }),
    })
  ).blob();
}

async function setPrivateMail(privateMail, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return await fetch(API_URL + "baseData/mail", {
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ privateMail: privateMail }),
  });
}

async function sendMail(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return await fetch(API_URL + "finalize/graph", {
    method: "GET",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
  });
}

async function sendFurthestSteps(step, substep, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return await fetch(API_URL + "state/step/" + step + "/substep/" + substep, {
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
  });
}

async function receiveFurthestSteps(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return (
    await fetch(API_URL + "state/latest", {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + idAndAccessToken.idToken,
        "X-BTB-Access-Token": idAndAccessToken.accessToken,
        "Content-Type": "application/json",
      },
    })
  ).json();
}

async function triggerSafetyStepsFinished(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return await fetch(API_URL + "compliance/security", {
    // this is the correct endpoint... it just uses the wrong word for "Sicherheit"
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
  });
}

async function triggerPrivacyStepsFinished(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return await fetch(API_URL + "compliance/privacy", {
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
  });
}

async function adminUpdatePDF(file, fileName, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(API_URL + "admin/upload/" + fileName, {
    method: "PATCH",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
    },
    body: formData,
  });

  if (response.ok) {
    return response.json();
  } else {
    return {};
  }
}

async function adminUploadPDF(formData, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const response = await fetch(API_URL + "admin/upload/", {
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
    },
    body: formData,
  });
  if (response.ok) {
    return response.json();
  } else {
    return {};
  }
}

async function adminShowPDF(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const response = await fetch(API_URL + "admin/showPDF", {
    method: "GET",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
      Pragma: "max-age=3600",
      "Cache-Control": "max-age=3600",
    },
  });
  return await response.json();
}

async function adminDeletePDF(fileName, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const response = await fetch(API_URL + "admin/delete/" + fileName, {
    method: "DELETE",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
  });
}

async function getAccountInfo(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const response = await fetch("https://graph.microsoft.com/v1.0/me", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idAndAccessToken.accessToken}`,
      "Content-Type": "application/json",
    },
  });
  const responseData = await response.json();

  return responseData;
}

async function adminShowUser(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const response = await fetch(API_URL + "userAccount", {
    method: "GET",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
      Pragma: "max-age=3600",
      "Cache-Control": "max-age=3600",
    },
  });
  return await response.json();
}

async function adminDeleteUser(userId, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const response = await fetch(API_URL + "admin/scheduleDeleteUser/" + userId, {
    method: "DELETE",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
  });
  return await response.text();
}

async function getUserData(userId, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const response = await fetch(API_URL + "userAccount/" + userId, {
    method: "GET",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
      Pragma: "max-age=3600",
      "Cache-Control": "max-age=3600",
    },
  });
  return await response.json();
}

async function saveStepData(stepData, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  await fetch(API_URL + "steps/save/", {
    method: "POST",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(stepData),
  });
}

async function updateStepData(stepData, stepNumber, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  await fetch(API_URL + "steps/" + stepNumber, {
    method: "PUT",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(stepData),
  });
}

async function getAllSteps(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const response = await fetch(API_URL + "steps", {
    method: "GET",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
  });

  return await response.json();
}

async function swapSteps(fromNum, toNum, instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  await fetch(API_URL + "steps/swap/" + fromNum + "/" + toNum, {
    method: "PATCH",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
    },
  });
}

async function getFileNames(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  const response = await fetch(API_URL + "pdf/names", {
    method: "GET",
    credentials: "include",
    headers: {
      Authorization: "Bearer " + idAndAccessToken.idToken,
      "X-BTB-Access-Token": idAndAccessToken.accessToken,
      "Content-Type": "application/json",
      Pragma: "max-age=3600",
      "Cache-Control": "max-age=3600",
    },
  });
  return await response.json();
}

async function getSecurityFilled(instance, inProgress) {
  const idAndAccessToken = await getTokensRaw(instance, inProgress);
  return (
    await fetch(API_URL + `upload/pdf/isFilled`, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: "Bearer " + idAndAccessToken.idToken,
        "X-BTB-Access-Token": idAndAccessToken.accessToken,
        "Content-Type": "application/json",
      },
    })
  ).json();
}

export default function useFetching() {
  const { instance, inProgress } = useMsal();
  const uploadDriversLicenseCallback = useCallback(
    function (base64EncodedPngImage, isFrontSide) {
      return uploadDriversLicense(
        base64EncodedPngImage,
        isFrontSide,
        instance,
        inProgress
      );
    },
    [instance, inProgress]
  );
  const getDriversLicenseCallback = useCallback(
    function () {
      return getDriversLicense(instance, inProgress);
    },
    [instance, inProgress]
  );
  const uploadSignatureCallback = useCallback(
    function (base64EncodedPngImage) {
      return uploadSignature(base64EncodedPngImage, instance, inProgress);
    },
    [instance, inProgress]
  );
  const getSignatureCallback = useCallback(
    function () {
      return getSignature(instance, inProgress);
    },
    [instance, inProgress]
  );
  const saveDataCallback = useCallback(
    function (jsonString) {
      return saveData(jsonString, instance, inProgress);
    },
    [instance, inProgress]
  );
  const getPdfCallback = useCallback(
    function (fileName, cacheType) {
      return getPdf(fileName, cacheType, instance, inProgress);
    },
    [instance, inProgress]
  );
  const uploadPdfCallback = useCallback(
    function (pdfFile) {
      return uploadPdf(pdfFile, instance, inProgress);
    },
    [instance, inProgress]
  );
  const getPdfRequiredAnnotationsCallback = useCallback(
    function (fileName) {
      return getPdfRequiredAnnotations(fileName, instance, inProgress);
    },
    [instance, inProgress]
  );
  const signPdfCallback = useCallback(
    function (fileName, annotations) {
      return signPdf(fileName, annotations, instance, inProgress);
    },
    [instance, inProgress]
  );
  const sendMailCallback = useCallback(
    function () {
      return sendMail(instance, inProgress);
    },
    [instance, inProgress]
  );
  const receiveFurthestStepsCallback = useCallback(
    function () {
      return receiveFurthestSteps(instance, inProgress);
    },
    [instance, inProgress]
  );
  const sendFurthestStepsCallback = useCallback(
    function (step, substep) {
      return sendFurthestSteps(step, substep, instance, inProgress);
    },
    [instance, inProgress]
  );
  const getBaseDataCallback = useCallback(
    function () {
      return getBaseData(instance, inProgress);
    },
    [instance, inProgress]
  );
  const setPrivateMailCallback = useCallback(
    function (privateMail) {
      return setPrivateMail(privateMail, instance, inProgress);
    },
    [instance, inProgress]
  );

  const triggerSafetyStepsFinishedCallback = useCallback(
    function () {
      return triggerSafetyStepsFinished(instance, inProgress);
    },
    [instance, inProgress]
  );
  const triggerPrivacyStepsFinishedCallback = useCallback(
    function () {
      return triggerPrivacyStepsFinished(instance, inProgress);
    },
    [instance, inProgress]
  );

  const adminUpdatePDFCallback = useCallback(
    function (file, fileName) {
      return adminUpdatePDF(file, fileName, instance, inProgress);
    },
    [instance, inProgress]
  );

  const adminUploadPDFCallback = useCallback(
    function (formData) {
      return adminUploadPDF(formData, instance, inProgress);
    },
    [instance, inProgress]
  );

  const adminShowPDFCallback = useCallback(
    function () {
      return adminShowPDF(instance, inProgress);
    },
    [instance, inProgress]
  );

  const adminDeletePDFCallback = useCallback(
    function (fileName) {
      return adminDeletePDF(fileName, instance, inProgress);
    },
    [instance, inProgress]
  );

  const getAccountInfoCallback = useCallback(
    function () {
      return getAccountInfo(instance, inProgress);
    },
    [instance, inProgress]
  );

  const adminShowUserCallback = useCallback(
    function () {
      return adminShowUser(instance, inProgress);
    },
    [instance, inProgress]
  );

  const adminDeleteUserCallback = useCallback(
    function (userId) {
      return adminDeleteUser(userId, instance, inProgress);
    },
    [instance, inProgress]
  );

  const getUserDataCallback = useCallback(
    function (userId) {
      return getUserData(userId, instance, inProgress);
    },
    [instance, inProgress]
  );

  const saveStepDataCallback = useCallback(
    function (stepData) {
      return saveStepData(stepData, instance, inProgress);
    },
    [instance, inProgress]
  );

  const updateStepDataCallback = useCallback(
    function (stepData, stepNumber) {
      return updateStepData(stepData, stepNumber, instance, inProgress);
    },
    [instance, inProgress]
  );

  const getAllStepsCallback = useCallback(
    function () {
      return getAllSteps(instance, inProgress);
    },
    [instance, inProgress]
  );

  const swapStepsCallback = useCallback(
    function (fromNum, toNum) {
      return swapSteps(fromNum, toNum, instance, inProgress);
    },
    [instance, inProgress]
  );

  const getFileNamesCallback = useCallback(
    function () {
      return getFileNames(instance, inProgress);
    },
    [instance, inProgress]
  );

  const getSecurityFilledCallback = useCallback(
    function () {
      return getSecurityFilled(instance, inProgress);
    },
    [instance, inProgress]
  );

  return {
    uploadDriversLicense: uploadDriversLicenseCallback,
    getDriversLicense: getDriversLicenseCallback,
    uploadSignature: uploadSignatureCallback,
    getSignature: getSignatureCallback,
    saveData: saveDataCallback,
    getPdf: getPdfCallback,
    uploadPdf: uploadPdfCallback,
    getPdfRequiredAnnotations: getPdfRequiredAnnotationsCallback,
    signPdf: signPdfCallback,
    sendMail: sendMailCallback,
    setPrivateMail: setPrivateMailCallback,
    receiveFurthestSteps: receiveFurthestStepsCallback,
    getBaseData: getBaseDataCallback,
    sendFurthestSteps: sendFurthestStepsCallback,
    triggerSafetyStepsFinished: triggerSafetyStepsFinishedCallback,
    triggerPrivacyStepsFinished: triggerPrivacyStepsFinishedCallback,
    adminUpdatePDF: adminUpdatePDFCallback,
    adminUploadPDF: adminUploadPDFCallback,
    adminShowPDF: adminShowPDFCallback,
    adminDeletePDF: adminDeletePDFCallback,
    getAccountInfo: getAccountInfoCallback,
    adminShowUser: adminShowUserCallback,
    adminDeleteUser: adminDeleteUserCallback,
    getUserData: getUserDataCallback,
    saveStepData: saveStepDataCallback,
    updateStepData: updateStepDataCallback,
    getAllSteps: getAllStepsCallback,
    swapSteps: swapStepsCallback,
    getFileNames: getFileNamesCallback,
    getSecurityFilled: getSecurityFilledCallback,
  };
}
